<script setup>
import { ref, onBeforeMount, onBeforeUnmount, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { API, HOST } from '@/utils/http'
import * as z from 'zod'
import parsePhoneNumber from 'libphonenumber-js'
import { useAuth } from '@/composables/useAuth'

import { Input } from '@/components/ui/input'
import { Form, FormField, FormItem, FormControl, FormLabel, FormMessage } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/toast'
import { SkylabPorthole } from '@/layouts'

import googlePlayBadge from '@/assets/img/google-play-badge.png'
import appStoreBadge from '@/assets/img/app-store-badge.svg'

const router = useRouter()
const route = useRoute()
const inviteCode = route.params.inviteCode
const isMapInvite = route.name === 'MapInvite'
const inviteBase = isMapInvite ? `${HOST}/map-invite` : `${HOST}/invite`
const targetLabel = isMapInvite ? 'map' : 'project'

const store = useStore()
const { initAuthListener } = useAuth()
const isLoggedIn = computed(() => store.getters.getIsLoggedIn)

const invite = ref(null)
const isShowForm = ref(false)
const isLoading = ref(true)
const isAccepting = ref(false)
const inviteError = ref(null)
const firebaseUser = ref(null)
const initialValues = ref(null)
let unsubscribeAuth = null

const targetName = computed(() => invite.value?.project_name || invite.value?.map_name || '')

const state = computed(() => {
  if (isLoading.value) return 'loading'
  if (isShowForm.value) return 'form'
  if (inviteError.value === 404) return 'not_found'
  if (inviteError.value === 403) return 'wrong_account'
  if (inviteError.value === 409) return 'accepted_signed_out'
  if (inviteError.value || !invite.value) return 'error'
  if (invite.value.is_accepted) return 'already_member'
  return firebaseUser.value ? 'ready' : 'ready_signed_out'
})

const phoneFieldSchema = z
  .string()
  .optional()
  .nullable()
  .transform((value, ctx) => {
    if (!value) return null

    const phoneNumber = parsePhoneNumber(value, { defaultCountry: 'US' })
    if (!phoneNumber?.isValid()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid or incomplete phone number',
      })
      return z.NEVER
    }

    return phoneNumber.number
  })

const formSchema = ref(z.object({
  first_name: z.string().min(1, 'First name is required').max(255),
  last_name: z.string().min(1, 'Last name is required').max(255),
  email: z.string().email('Invalid email address').optional().nullable(),
  phone: phoneFieldSchema,
}))

const signInRoute = () => ({ name: 'SignIn', query: { redirect: `${route.path}?accept=1` } })

const failToast = (description) => toast({ title: 'Unable to join', description, variant: 'destructive' })

const apiErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.detail || fallback

const getInvite = async (firebaseToken) => {
  try {
    const response = await API({
      method: 'GET',
      url: firebaseToken
        ? `${inviteBase}/${inviteCode}?firebase_token=${firebaseToken}`
        : `${inviteBase}/${inviteCode}`
    })
    invite.value = response.data || null
    inviteError.value = null
  } catch (error) {
    invite.value = null
    inviteError.value = error.response?.status || 'network'
  }
}

const retry = async () => {
  isLoading.value = true
  await getInvite(firebaseUser.value ? await firebaseUser.value.getIdToken(true) : null)
  isLoading.value = false
}

/** Make sure the backend session (JWT + vuex member) exists for the current Firebase user. */
const ensureSession = async () => {
  if (isLoggedIn.value) return { success: true }
  const firebaseToken = await firebaseUser.value.getIdToken(true)
  await store.dispatch('user/setupSettings')
  await store.dispatch('user/setUser', firebaseUser.value)
  store.commit('SET_FIREBASE_TOKEN', firebaseToken)
  return store.dispatch('user/handleAuthentication', { firebaseToken })
}

const openTarget = async () => {
  const orgId = invite.value?.organization_id
  if (!orgId) {
    router.push({ name: 'Dashboard' })
    return
  }
  await store.dispatch('org/fetchOrgs')
  await store.dispatch('org/setSelectedOrg', String(orgId))
  if (isMapInvite) {
    router.push(`/organizations/${orgId}/maps`)
    return
  }
  router.push({ name: 'Project', params: { org_id: orgId, project_id: invite.value.project_id } })
}

const splitName = (displayName) => {
  const parts = (displayName || '').trim().split(/\s+/).filter(Boolean)
  return { first: parts[0] || '', last: parts.slice(1).join(' ') }
}

const buildValues = () => {
  const member = store.getters['user/getMember']
  const fbUser = firebaseUser.value
  const derived = splitName(member?.display_name || fbUser?.displayName)
  return {
    first_name: invite.value?.first_name || member?.first_name || derived.first,
    last_name: invite.value?.last_name || member?.last_name || derived.last,
    email: invite.value?.email || member?.email || fbUser?.email || '',
    phone: invite.value?.phone || member?.phone || fbUser?.phoneNumber || ''
  }
}

const joinProject = async () => {
  if (!firebaseUser.value) {
    router.push(signInRoute())
    return
  }
  if (isAccepting.value) return

  isAccepting.value = true
  try {
    const session = await ensureSession()

    if (invite.value?.is_accepted) {
      if (session.success) await openTarget()
      else failToast('Could not sign you in. Please try again.')
      return
    }

    // Registered users accept with their session; new users register through onSubmit.
    if (session.success && !isMapInvite) {
      await API({ method: 'PUT', url: `${inviteBase}/${inviteCode}` })
      await openTarget()
      return
    }

    const values = buildValues()
    if (values.first_name && values.last_name) {
      await onSubmit(values)
      return
    }
    initialValues.value = values
    isShowForm.value = true
  } catch (error) {
    failToast(apiErrorMessage(error, 'Failed to process invitation. Please try again.'))
  } finally {
    isAccepting.value = false
  }
}

async function onSubmit(values) {
  try {
    const idToken = await firebaseUser.value.getIdToken(true)

    const result = await store.dispatch('user/handleAuthentication', {
      firebaseToken: idToken,
      inviteCode: inviteCode,
      inviteType: isMapInvite ? 'map' : null,
      inviteDetails: {
        first_name: values.first_name,
        last_name: values.last_name,
        email: values.email,
        phone: values.phone
      }
    })

    if (result.success) {
      await openTarget()
      return
    }

    failToast(result.reason === 'error'
      ? apiErrorMessage(result.error, 'Failed to process invitation. Please try again.')
      : `Authentication failed: ${result.reason}`)
  } catch (error) {
    failToast(apiErrorMessage(error, 'Failed to process invitation. Please try again.'))
  }
}

const back = () => {
  isShowForm.value = false
}

const close = async () => {
  if (!firebaseUser.value) {
    router.push({ name: 'SignIn' })
    return
  }
  const session = await ensureSession()
  router.push(session.success ? { name: 'Dashboard' } : { name: 'SignIn' })
}

const switchAccount = async () => {
  await store.dispatch('logout')
  router.push(signInRoute())
}

onBeforeMount(() => {
  unsubscribeAuth = initAuthListener(async (user) => {
    firebaseUser.value = user
    await getInvite(user ? await user.getIdToken() : null)
    isLoading.value = false

    const shouldContinue = user && route.query.accept === '1' && ['ready', 'already_member'].includes(state.value)
    if (shouldContinue) {
      router.replace({ path: route.path })
      joinProject()
    }
  })
})

onBeforeUnmount(() => {
  if (unsubscribeAuth) unsubscribeAuth()
})
</script>

<template>
  <SkylabPorthole>
    <div class="w-full max-w-sm bg-card text-foreground rounded-2xl border border-border shadow-[0_40px_100px_-15px_rgba(0,0,0,0.9),0_0_120px_-20px_rgba(255,255,255,0.15)] px-6 py-6 overflow-hidden">
    <div>
      <div v-if="state === 'loading'" class="md:p-1 text-center">
        <p class="mt-4 mb-3 text-gray-500">Loading invite...</p>
      </div>

      <div v-else-if="state === 'not_found'" class="md:p-1 text-center">
        <p class="mt-4 mb-3">This invite link is invalid or has expired.</p>
        <Button @click="close">Close</Button>
      </div>

      <div v-else-if="state === 'wrong_account'" class="md:p-1 text-center">
        <p class="mt-4 mb-1">This invite was sent to a different email address.</p>
        <p class="mb-4 text-sm text-gray-500">You are signed in as <b>{{ firebaseUser?.email || firebaseUser?.phoneNumber }}</b>.</p>
        <div class="space-x-2">
          <Button @click="switchAccount">Switch account</Button>
          <Button variant="outline" @click="close">Close</Button>
        </div>
      </div>

      <div v-else-if="state === 'accepted_signed_out'" class="md:p-1 text-center">
        <p class="mt-4 mb-3">This invite has already been accepted. Sign in to open the {{ targetLabel }}.</p>
        <Button @click="router.push(signInRoute())">Sign in</Button>
      </div>

      <div v-else-if="state === 'already_member'" class="md:p-1 text-center">
        <p class="mt-4 mb-3">You're already a member of <b>{{ targetName }}</b>.</p>
        <Button :disabled="isAccepting" @click="joinProject">Open {{ targetLabel }}</Button>
      </div>

      <div v-else-if="state === 'ready_signed_out'" class="md:p-1 mb-4 text-left">
        <div class="mt-4">
          <p>
            {{ invite.inviter_name }} invited you to join <br />
            the <b>{{ targetName }}</b> {{ targetLabel }}.
          </p>
        </div>
        <div class="space-x-2 mt-6">
          <Button size="lg" class="font-semibold" @click="joinProject">Sign in to accept</Button>
          <Button size="lg" variant="outline" @click="close">Close</Button>
        </div>
      </div>

      <div v-else-if="state === 'ready'" class="md:p-1 mb-4 text-left">
        <div class="mt-4">
          <h3 class="mb-2">Hi, <b>{{ invite.fullname }}</b></h3>
          <p>
            {{ invite.inviter_name }} invited you to join <br />
            the <b>{{ targetName }}</b> {{ targetLabel }}.
          </p>
          <p class="mt-2 text-sm text-gray-500">Accepting as {{ firebaseUser?.email || firebaseUser?.phoneNumber }}</p>
        </div>
        <div class="space-x-2 mt-6">
          <Button size="lg" class="font-semibold" :disabled="isAccepting" @click="joinProject">
            {{ isAccepting ? 'Joining...' : `Accept & open ${targetLabel}` }}
          </Button>
          <Button size="lg" variant="outline" @click="close">Close</Button>
        </div>
        <button type="button" class="mt-3 text-sm text-gray-500 underline" @click="switchAccount">Not you? Switch account</button>
      </div>

      <div v-else-if="state === 'form'">
        <div class="mb-3">
          <h2 class="font-bold">Complete your profile</h2>
          <p class="text-gray-500 text-sm">Please provide your details to complete your profile</p>
        </div>

        <Form
          v-if="initialValues"
          :form-schema="formSchema"
          :initial-values="initialValues"
          @submit="onSubmit"
          class="space-y-4"
        >
          <FormField name="first_name" v-slot="{ componentField }">
            <FormItem>
              <FormLabel>First Name</FormLabel>
              <FormControl>
                <Input v-bind="componentField" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>

          <FormField name="last_name" v-slot="{ componentField }">
            <FormItem>
              <FormLabel>Last Name</FormLabel>
              <FormControl>
                <Input v-bind="componentField" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>

          <FormField name="email" v-slot="{ componentField }">
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input v-bind="componentField" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>

          <FormField name="phone" v-slot="{ componentField }">
            <FormItem>
              <FormLabel>Phone</FormLabel>
              <FormControl>
                <Input v-bind="componentField" />
              </FormControl>
              <FormMessage />
            </FormItem>
          </FormField>

          <div class="flex justify-end space-x-2">
            <Button type="button" variant="ghost" @click="back">Back</Button>
            <Button type="submit">Save</Button>
          </div>
        </Form>
      </div>

      <div v-else class="md:p-1 text-center">
        <p class="mt-4 mb-3">We couldn't load this invite. Check your connection and try again.</p>
        <div class="space-x-2">
          <Button @click="retry">Retry</Button>
          <Button variant="outline" @click="close">Close</Button>
        </div>
      </div>
    </div>

    <!-- App store links -->
    <div class="mt-10 pt-6 border-t flex flex-col items-center gap-3">
      <p class="text-sm text-gray-400">Also available on mobile</p>
      <div class="flex items-center gap-4">
        <a href="https://play.google.com/store/apps/details?id=com.earthpbc.explorer" target="_blank" rel="noopener noreferrer">
          <img :src="googlePlayBadge" alt="Get it on Google Play" class="h-10" />
        </a>
        <a href="https://apps.apple.com/us/app/earth-pbc/id1590563602" target="_blank" rel="noopener noreferrer">
          <img :src="appStoreBadge" alt="Download on the App Store" class="h-8" />
        </a>
      </div>
    </div>
    </div>
  </SkylabPorthole>
</template>
